import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import {FREE_PLAN_NAME} from '../../constants/plan';
import {BILLING_CYCLE, BillingCycle} from '../constants/plans';

type SelectedPlan = {
  type: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  billingCycle: BillingCycle;
  countryCode: string;
};

export type AccountSignupFormValues = {
  businessType: string | null;
  businessTitle: string | null;
  clientType?: string;
  hasAccount?: boolean;
  businessSubType?: string;
  squareFootage?: string;
  selectedPlan: SelectedPlan;
  preSelectedPlan?: string;
  preSelectedBillingCycle?: BillingCycle;
  userId?: number;
  teamId?: number;
};

type AccountSignupContextType = {
  formValues: AccountSignupFormValues;
  updateFormValues: (updates: Partial<AccountSignupFormValues>) => void;
  resetFormValues: () => void;
};

const AccountSignupContext = createContext<
  AccountSignupContextType | undefined
>(undefined);

export const initialFormValues: AccountSignupFormValues = {
  businessType: null,
  businessTitle: null,
  clientType: undefined,
  businessSubType: undefined,
  squareFootage: undefined,
  selectedPlan: {
    type: FREE_PLAN_NAME,
    name: FREE_PLAN_NAME,
    monthlyPrice: 0,
    annualPrice: 0,
    billingCycle: BILLING_CYCLE[0],
    countryCode: 'US',
  },
};

export const AccountSignupProvider = ({children}: {children: ReactNode}) => {
  const [formValues, setFormValues] =
    useState<AccountSignupFormValues>(initialFormValues);

  const updateFormValues = useCallback(
    (updates: Partial<AccountSignupFormValues>) => {
      setFormValues((prev) => ({...prev, ...updates}));
    },
    []
  );

  const resetFormValues = useCallback(() => {
    setFormValues(initialFormValues);
  }, []);

  const value = useMemo(
    () => ({formValues, updateFormValues, resetFormValues}),
    [formValues, updateFormValues, resetFormValues]
  );

  return (
    <AccountSignupContext.Provider value={value}>
      {children}
    </AccountSignupContext.Provider>
  );
};

export const useAccountSignup = () => {
  const context = useContext(AccountSignupContext);
  if (context === undefined) {
    throw new Error(
      'useAccountSignup must be used within an AccountSignupProvider'
    );
  }
  return context;
};
